import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { Component, OnInit, OnDestroy } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { MatDialog } from '@angular/material/dialog'
import { BlockDataService } from '../../../block-services/block-data.service'
import { BlockFieldsService } from '../../../block-services/block-fields.service'
import { BuilderService } from '../../../builder-services/builder.service'
import { LoginService } from '../../../login/login.service'
import { PagesService } from '../pages.service'
import { AppSettingsService } from 'src/app/settings/services/app-settings.service'
import { WebsiteTemplatesService } from '../website-templates.service'
import { BehaviorSubject } from 'rxjs'
import { environment } from 'src/environments/environment.staging'

@Component({
  selector: 'website-page-editor',
  templateUrl: './website-page-editor.component.html',
  styleUrls: ['./website-page-editor.component.scss']
})
export class WebsitePageEditorComponent implements OnInit, OnDestroy {
  private bootstrapLink: HTMLLinkElement | null = null
  debuggableJSON = environment.debuggableJSON
  mobile = false
  openSettings = false
  currentPage: any = null
  pageId: string = ''
  isLoading = false
  private readonly templateApplyStarted$ = new BehaviorSubject(false)

  constructor(
    private route: ActivatedRoute,
    public blockDataService: BlockDataService,
    private blockFieldService: BlockFieldsService,
    public loginService: LoginService,
    public builderService: BuilderService,
    public pagesService: PagesService,
    public dialog: MatDialog,
    private appSettingsService: AppSettingsService,
    private websiteTemplatesService: WebsiteTemplatesService
  ) {
    this.blockDataService.getCurrentBlock().subscribe((currentBlockData) => {
      this.openSettings = true
    })

    // Subscribe to block data changes for change detection
    this.blockDataService.getBlockData().subscribe((blockTextData) => {
      // Trigger change detection by creating a new array reference
      this.blockDataService.blocksData = [...this.blockDataService.blocksData];
    })
  }

  ngOnInit() {
    // Load Bootstrap CSS dynamically for website preview only
    this.loadBootstrapCSS()

    if (window.screen.width <= 768) {
      this.mobile = true
    }

    // Apply color settings from app settings
    this.applyColors()

    // Get page ID from route params or selected view
    this.route.queryParams.subscribe(params => {
      this.pageId = params['pageId']
      if (this.pageId) {
        this.loadPage(this.pageId)
      } else {
        // If no pageId provided, load the first available page or show empty state
        this.loadDefaultPage()
      }
    })
  }

  ngOnDestroy() {
    // Remove Bootstrap CSS when component is destroyed
    this.removeBootstrapCSS()
  }

  private loadBootstrapCSS() {
    // Check if Bootstrap is already loaded
    const existingLink = document.getElementById('bootstrap-css-website-builder')
    if (!existingLink) {
      this.bootstrapLink = document.createElement('link')
      this.bootstrapLink.id = 'bootstrap-css-website-builder'
      this.bootstrapLink.rel = 'stylesheet'
      this.bootstrapLink.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css'
      document.head.appendChild(this.bootstrapLink)
    }
  }

  private removeBootstrapCSS() {
    if (this.bootstrapLink) {
      document.head.removeChild(this.bootstrapLink)
      this.bootstrapLink = null
    } else {
      // Fallback: remove by ID in case reference was lost
      const link = document.getElementById('bootstrap-css-website-builder')
      if (link) {
        document.head.removeChild(link)
      }
    }
  }

  /**
   * Load the default page (first page or home page)
   */
  loadDefaultPage() {
    const projectId = this.builderService.selectedProject
    if (!projectId) return

    this.isLoading = true
    this.pagesService.getPages(projectId).subscribe(
      (pages: any) => {
        if (pages && typeof pages === 'object') {
          const pagesArray = this.pagesService.allPagesFormatter(pages, projectId)

          if (pagesArray.length > 0) {
            // Try to find a page named "home" first, otherwise use the first page
            const homePage = pagesArray.find((p: any) =>
              p.page_name?.toLowerCase() === 'home' ||
              p.page_id?.toLowerCase() === 'home'
            )
            const defaultPage = homePage || pagesArray[0]

            // Load the default page
            this.pageId = defaultPage.page_id
            this.loadPage(this.pageId)
          } else {
            // No pages exist, create a default home page
            this.createDefaultHomePage()
          }
        } else {
          // No pages at all, create a default home page
          this.createDefaultHomePage()
        }
      },
      (error) => {
        console.error('Error loading default page:', error)
        this.isLoading = false
      }
    )
  }

  /**
   * Create default pages if no pages exist
   * Creates all default pages: home, shop, contact, cart, checkout, about
   */
  createDefaultHomePage() {
    const projectId = this.builderService.selectedProject
    if (!projectId || this.templateApplyStarted$.value) return

    this.templateApplyStarted$.next(true)
    this.applyFirstWebsiteTemplate(projectId)
  }

  createFallbackDefaultPages(projectId: string) {
    console.log('Creating default pages...')
    this.pagesService.createDefaultPages(projectId, this.blockDataService).subscribe(
      () => {
        console.log('Default pages created successfully')
        this.loadPagesAfterTemplateApplication(projectId)
      },
      (error) => {
        console.error('Error creating default pages:', error)
        this.isLoading = false
        this.blockDataService.blocksData = []
      }
    )
  }

  /**
   * Apply the first website template from Firebase to the newly created project
   */
  applyFirstWebsiteTemplate(projectId: string) {
    console.log('Fetching website templates to apply first template...')

    this.websiteTemplatesService.getTemplates().subscribe(
      (templates: any) => {
        if (!templates || Object.keys(templates).length === 0) {
          console.log('No website templates found. Creating default pages.')
          this.createFallbackDefaultPages(projectId)
          return
        }

        // Get the first template
        const templateKeys = Object.keys(templates)
        const firstTemplateKey = templateKeys[0]
        const firstTemplate = templates[firstTemplateKey]

        console.log('Applying first template:', firstTemplate.template_name)

        const pages = firstTemplate.pages || {}
        const statics = firstTemplate.statics || {}
        const headerData = statics.header || {}
        const footerData = statics.footer || {}

        // Apply the template pages to the project
        const pageUpserts = Object.values(pages as Record<string, any>).map((page: any) =>
          this.pagesService.upsertPage(projectId, page)
        )
        Promise.all([
          ...pageUpserts,
          this.pagesService.setStaticBlock(projectId, 'header', headerData).toPromise(),
          this.pagesService.setStaticBlock(projectId, 'footer', footerData).toPromise(),
        ])
          .then(() => {
            console.log('First website template applied successfully!')
            this.loadPagesAfterTemplateApplication(projectId)
          })
          .catch((error) => {
            console.error('Error applying template:', error)
            this.loadPagesAfterTemplateApplication(projectId)
          })
      },
      (error) => {
        console.error('Error fetching website templates:', error)
        this.createFallbackDefaultPages(projectId)
      }
    )
  }

  /**
   * Load pages after template application and navigate to home page
   */
  loadPagesAfterTemplateApplication(projectId: string) {
    this.pagesService.getPages(projectId).subscribe(
      (pages: any) => {
        if (pages && typeof pages === 'object') {
          const pagesArray = this.pagesService.allPagesFormatter(pages, projectId)

          // Find and load the home page
          const homePage = pagesArray.find((p: any) =>
            p.page_name?.toLowerCase() === 'home'
          )

          if (homePage) {
            this.pageId = homePage.page_id
            this.loadPage(homePage.page_id)
          } else {
            // Fallback to first page if home not found
            this.pageId = pagesArray[0].page_id
            this.loadPage(pagesArray[0].page_id)
          }
        }
      },
      (error) => {
        console.error('Error loading pages after creation:', error)
        this.isLoading = false
        this.blockDataService.blocksData = []
      }
    )
  }

  /**
   * Load page data from Firebase
   */
  loadPage(pageId: string) {
    this.isLoading = true
    const projectId = this.builderService.selectedProject
    console.log('Loading page with ID:', pageId)
    this.pagesService.getPageById(projectId, pageId).subscribe(
      (response: any) => {
        console.log('Loaded page:', response);
        this.currentPage = response

        // Set header button to show website page editor controls
        this.builderService.headerBtn = 'websitePageEditor'

        // Store selected page data in blockDataService for header to access
        this.blockDataService.sendSelectedWebsitePage({
          title: response.page_name || 'Untitled Page',
          page_id: pageId,
          reloadPages: true // Signal header to reload pages
        })

        // Load blocks into the builder
        // Check both all_blocks and allBlocks (Firebase might use camelCase)
        const blocksData = response.all_blocks || response.allBlocks

        if (blocksData) {
          try {
            const blocks = typeof blocksData === 'string'
              ? JSON.parse(blocksData)
              : blocksData
            console.log('Parsed blocks:', blocks)

            // Load static header if not already in blocks
            this.loadStaticHeaderIfNeeded(blocks)
          } catch (error) {
            console.error('Error parsing blocks:', error)
            console.error('Raw blocks data:', blocksData)
            this.blockDataService.blocksData = []
            this.isLoading = false
          }
        } else {
          console.warn('No blocks data found in response:', response)
          // Load static header even with empty blocks
          this.loadStaticHeaderIfNeeded([])
        }
      },
      (error) => {
        console.error('Error loading page:', error)
        this.isLoading = false
      }
    )
  }

  /**
   * Load static header from Firebase and prepend to blocks if not already present
   */
  private loadStaticHeaderIfNeeded(blocks: any[]) {
    const hasHeader = blocks.some((block: any) => block.type === 'header')

    if (!hasHeader) {
      // Load static header from Firebase
      this.pagesService.getStaticBlock(this.builderService.selectedProject, 'header').subscribe(
        (staticHeader: any) => {
          if (staticHeader && Object.keys(staticHeader).length > 0) {
            // Add header to blocks array with consistent 'static-header' ID
            const headerBlock = {
              ...staticHeader,
              id: 'static-header',
              type: 'header'
            }
            this.blockDataService.blocksData = [headerBlock, ...blocks]
          } else {
            // No static header found, just use the blocks as-is
            this.blockDataService.blocksData = blocks
          }
          // Load footer after header is loaded
          this.loadStaticFooterIfNeeded()
        },
        (error) => {
          console.error('Error loading static header:', error)
          // Fallback: just use the blocks without header
          this.blockDataService.blocksData = blocks
          // Still try to load footer even if header failed
          this.loadStaticFooterIfNeeded()
        }
      )
    } else {
      // Header already exists, normalize its ID to 'static-header'
      const headerIndex = blocks.findIndex((block: any) => block.type === 'header')
      if (headerIndex !== -1) {
        blocks[headerIndex].id = 'static-header'
      }
      this.blockDataService.blocksData = blocks
      // Load footer after setting blocks
      this.loadStaticFooterIfNeeded()
    }
  }

  /**
   * Load static footer from Firebase and append to blocks if not already present
   */
  private loadStaticFooterIfNeeded() {
    const hasFooter = this.blockDataService.blocksData.some((block: any) => block.type === 'footer')

    if (!hasFooter) {
      // Load static footer from Firebase
      this.pagesService.getStaticBlock(this.builderService.selectedProject, 'footer').subscribe(
        (staticFooter: any) => {
          if (staticFooter && Object.keys(staticFooter).length > 0) {
            // Add footer to blocks array with consistent 'static-footer' ID
            const footerBlock = {
              ...staticFooter,
              id: 'static-footer',
              type: 'footer'
            }
            this.blockDataService.blocksData = [...this.blockDataService.blocksData, footerBlock]
          }
          this.isLoading = false
        },
        (error) => {
          console.error('Error loading static footer:', error)
          this.isLoading = false
        }
      )
    } else {
      // Footer already exists, normalize its ID to 'static-footer'
      const footerIndex = this.blockDataService.blocksData.findIndex((block: any) => block.type === 'footer')
      if (footerIndex !== -1) {
        this.blockDataService.blocksData[footerIndex].id = 'static-footer'
      }
      this.isLoading = false
    }
  }

  /**
   * Close the Settings popup (mobile view)
   */
  closeSettings() {
    this.openSettings = false
  }

  /**
   * Get element index for drag and drop
   */
  getElementIndex = (id: string) => {
    let modifiedId = 'blockid' + id
    var child = document.getElementById(modifiedId)?.parentNode?.parentNode
    var parent = child?.parentNode
    return Array.prototype.indexOf.call(parent?.children, child)
  }

  /**
   * Handle drop event for blocks
   */
  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      // Moving within the same container (reordering)
      // We need to work with the draggable blocks array, not the full array
      const draggableBlocks = this.getDraggableBlocks()

      // Find the actual index of the dragged item by its data
      // event.item.data might be just the ID string or the full object
      const draggedId = typeof event.item.data === 'string' ? event.item.data : event.item.data.id
      const actualPreviousIndex = draggableBlocks.findIndex((b: any) => b.id === draggedId)

      // Reorder the draggable blocks using the correct index
      moveItemInArray(
        draggableBlocks,
        actualPreviousIndex,
        event.currentIndex
      )

      // Rebuild the full blocks array with header, footer, and the above-navbar
      // announcement bar (if any) restored in their static positions - none of
      // these are part of getDraggableBlocks() so they'd otherwise be dropped here
      const header = this.blockDataService.blocksData.find((b: any) => b.type === 'header')
      const footer = this.blockDataService.blocksData.find((b: any) => b.type === 'footer')
      const aboveNavbarBar = this.blockDataService.blocksData.find(
        (b: any) => b.type === 'announcementBar' && b.data?.position === 'above-navbar'
      )

      this.blockDataService.blocksData = [
        ...(header ? [header] : []),
        ...(aboveNavbarBar ? [aboveNavbarBar] : []),
        ...draggableBlocks,
        ...(footer ? [footer] : [])
      ]
    } else {
      // Copying from sidebar to page
      // Get the tag name of the dropped element to determine block type
      let droppedBlock = event.item.element.nativeElement.tagName
      let droppedBlockData
      let targetIndex // Don't use event.currentIndex, we'll set it based on our logic

      // Get the appropriate block data based on the component type
      if (droppedBlock === 'BLOCK-TEXT') {
        droppedBlockData = this.blockFieldService.getTextBlockFields()
      } else if (droppedBlock === 'BLOCK-IMAGE') {
        droppedBlockData = this.blockFieldService.getImageBlockFields()
      } else if (droppedBlock === 'BLOCK-SECTION') {
        droppedBlockData = this.blockFieldService.getSectionBlockFields()
      } else if (droppedBlock === 'BLOCK-PRODUCTS') {
        droppedBlockData = this.blockFieldService.getProductsBlockFields()
      } else if (droppedBlock === 'BLOCK-CATEGORIES') {
        droppedBlockData = this.blockFieldService.getCategoriesBlockFields()
      } else if (droppedBlock === 'BLOCK-POSTS') {
        droppedBlockData = this.blockFieldService.getPostsBlockFields()
      } else if (droppedBlock === 'BLOCK-HEADER') {
        droppedBlockData = this.blockFieldService.getHeaderBlockFields()
        // Header must always be first (index 0)
        targetIndex = 0
      } else if (droppedBlock === 'BLOCK-FOOTER') {
        droppedBlockData = this.blockFieldService.getFooterBlockFields()
        // Footer must always be last
        targetIndex = this.blockDataService.blocksData.length
      } else if (droppedBlock === 'BLOCK-VIDEO') {
        droppedBlockData = this.blockFieldService.getVideoBlockFields()
      } else if (droppedBlock === 'BLOCK-SLIDER') {
        droppedBlockData = this.blockFieldService.getSliderBlockFields()
      } else if (droppedBlock === 'BLOCK-BUTTON') {
        droppedBlockData = this.blockFieldService.buttonBlockData
      } else if (droppedBlock === 'BLOCK-FLOATING-SOCIAL') {
        droppedBlockData = this.blockFieldService.floatingSocialBlockData
      } else if (droppedBlock === 'BLOCK-STATIC') {
        droppedBlockData = this.blockFieldService.staticBlockData
      } else if (droppedBlock === 'BLOCK-REVIEWS') {
        droppedBlockData = this.blockFieldService.getReviewsBlockFields()
      } else if (droppedBlock === 'BLOCK-ANNOUNCEMENT-BAR') {
        droppedBlockData = this.blockFieldService.getAnnouncementBarBlockFields()
        // Defaults to the very start of the content, right after the header, so it reads
        // as a banner above the rest of the page - still freely draggable/deletable after.
        const hasHeaderForBar = this.blockDataService.blocksData.length > 0 &&
                                 this.blockDataService.blocksData[0]?.type === 'header'
        targetIndex = hasHeaderForBar ? 1 : 0
      } else {
        // Default to text block
        droppedBlockData = this.blockFieldService.getTextBlockFields()
      }

      // Clone the block data to avoid mutations
      droppedBlockData = { ...droppedBlockData }

      // Generate a unique ID for the new block
      droppedBlockData.id = this.blockDataService.getUniqueId(2)

      // Set a consistent ID for headers
      if (droppedBlock === 'BLOCK-HEADER') {
        // Use consistent 'static-header' ID instead of random ID
        droppedBlockData.id = 'static-header'
        // Header always goes to index 0 (already set above)
        console.log('Inserting header at index:', targetIndex)
      } else if (droppedBlock === 'BLOCK-FOOTER') {
        droppedBlockData.id = 'static-footer';
        // Footer always goes to the end (already set above)
        console.log('Inserting footer at index:', targetIndex)
      } else if (droppedBlock === 'BLOCK-ANNOUNCEMENT-BAR') {
        // Start-of-content index already computed above - don't let the generic
        // "insert before footer" logic below override it.
        console.log('Inserting announcement bar at index:', targetIndex)
      } else {
        // For all other blocks, insert before footer (or at end if no footer)
        const hasFooter = this.blockDataService.blocksData.length > 0 &&
                          this.blockDataService.blocksData[this.blockDataService.blocksData.length - 1]?.type === 'footer'

        console.log('Current blocks length:', this.blockDataService.blocksData.length)
        console.log('Has footer:', hasFooter)

        // Default insertion point: before footer if exists, otherwise at end
        if (hasFooter) {
          targetIndex = this.blockDataService.blocksData.length - 1
        } else {
          targetIndex = this.blockDataService.blocksData.length
        }

        // Ensure block is positioned after header (if exists)
        const hasHeader = this.blockDataService.blocksData.length > 0 &&
                          this.blockDataService.blocksData[0]?.type === 'header'

        if (hasHeader && targetIndex === 0) {
          targetIndex = 1
        }

        console.log('Inserting', droppedBlock, 'at index:', targetIndex)
      }

      // Insert the block at the specified index
      this.blockDataService.blocksData.splice(targetIndex, 0, droppedBlockData)
      console.log('Blocks after insertion:', this.blockDataService.blocksData.map((b: any) => b.type))

      // Trigger change detection by creating a new reference
      this.blockDataService.blocksData = [...this.blockDataService.blocksData]
    }
  }

  /**
   * Get draggable blocks (excludes header, footer, and the announcement bar
   * when it's set to render above the navbar - that one lives in its own
   * static slot, same as header/footer)
   */
  getDraggableBlocks() {
    return this.blockDataService.blocksData.filter(
      (block: any) => block.type !== 'header' && block.type !== 'footer' &&
        !(block.type === 'announcementBar' && block.data?.position === 'above-navbar')
    )
  }

  /**
   * TrackBy function for blocks to improve performance and fix drag-drop indices
   */
  trackByBlockId(_index: number, block: any): any {
    return block.id
  }

  /**
   * Get connected drop list IDs - includes section column IDs for drag-drop into sections
   */
  getColumnsIds(): string[] {
    // Get dynamic section column IDs from the DOM
    const sectionColumnIds = this.blockDataService.getColumnIds()
    // Return all connected drop list IDs
    return [...sectionColumnIds]
  }

  /**
   * Toggle blocks sidebar (mobile)
   */
  blocksHome() {
    this.builderService.blocksCollapsed = !this.builderService.blocksCollapsed
  }

  /**
   * Check if the header template is template5 (slim sidebar layout)
   */
  isTemplate5Active(): boolean {
    const headerBlock = this.blockDataService.blocksData.find((block: any) => block.type === 'header')
    return headerBlock?.data?.headerTemplate === 'template5'
  }

  /**
   * Check if the header template is template6 (full sidebar layout)
   */
  isTemplate6Active(): boolean {
    const headerBlock = this.blockDataService.blocksData.find((block: any) => block.type === 'header')
    return headerBlock?.data?.headerTemplate === 'template6'
  }

  /**
   * Apply the app settings colors to the website preview
   */
  applyColors = () => {
    this.appSettingsService.getSettingsByType(this.builderService.selectedProject, 'application_settings').then((application_settings: any) => {
      const mobile_primary_color = application_settings?.mobile_primary_color
      const mobile_text_color    = application_settings?.mobile_text_color
      if (!mobile_primary_color) return
      const r = parseInt(mobile_primary_color.slice(1, 3), 16)
      const g = parseInt(mobile_primary_color.slice(3, 5), 16)
      const b = parseInt(mobile_primary_color.slice(5, 7), 16)
      const style = document.createElement('style')
      style.innerHTML = `
          :root {
            --ion-color-primary: ${mobile_primary_color} !important;
            --ion-color-primary-opacity: rgba(${r}, ${g}, ${b}, 15%) !important;
            --ion-color-text: ${mobile_text_color} !important;
            --bs-primary: ${mobile_primary_color} !important;
            --bs-primary-rgb: ${r}, ${g}, ${b} !important;
          }
          .btn-primary {
            --bs-btn-bg: ${mobile_primary_color} !important;
            --bs-btn-border-color: ${mobile_primary_color} !important;
            --bs-btn-hover-bg: ${mobile_primary_color} !important;
            --bs-btn-hover-border-color: ${mobile_primary_color} !important;
            --bs-btn-active-bg: ${mobile_primary_color} !important;
            --bs-btn-active-border-color: ${mobile_primary_color} !important;
          }
          `
      document.body.appendChild(style)
    })
  }
}
